"use client";

import { useIsRestoring, useQuery } from "@tanstack/react-query";
import { TenantApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import {
  transformPersonalizationResponse,
  type PersonalizationSettings,
} from "@/lib/utils/personalization";
import {
  getPersonalizationFromStorage,
  savePersonalizationToStorage,
  getPersonalizationStorageAge,
} from "@/lib/utils/personalization-storage";
import { createOpenApiConfiguration } from "@/lib/utils";

interface IUseTenantPersonalizationGetProps {
  tenantId: number;
}

export const useTenantPersonalizationGet = (
  { tenantId }: IUseTenantPersonalizationGetProps,
  options?: {
    enabled?: boolean;
    initialData?: PersonalizationSettings | null;
    staleTime?: number;
    refetchOnMount?: boolean | "always";
    refetchOnWindowFocus?: boolean;
  },
) => {
  // Get localStorage data as placeholder (if available and fresh)
  // This gives instant UI update while we fetch from server
  const localStorageData = typeof window !== "undefined" 
    ? getPersonalizationFromStorage(tenantId)
    : null;
  const isRestoring = useIsRestoring();

  const { data, isError, isLoading, isSuccess, isPending } = useQuery({
    queryKey: [QueryKeys.TenantPersonalization, tenantId],
    queryFn: async () => {
      // Always fetch from server to get latest data
      const service = new TenantApi(createOpenApiConfiguration());
      
      const response = await service.apiAccountTenantIdPersonalizationGet({
        tenantId,
      });
      
      const transformed = transformPersonalizationResponse(response);
      
      // Check if we have fresh localStorage data that differs from server
      const storedData = getPersonalizationFromStorage(tenantId);
      if (storedData && transformed) {
        const differsFromServer =
          storedData.colorPalette !== transformed.colorPalette ||
          (storedData.customColor ?? null) !== (transformed.customColor ?? null) ||
          storedData.grainEnabled !== transformed.grainEnabled ||
          storedData.gradientEnabled !== transformed.gradientEnabled ||
          storedData.themeModeControl !== transformed.themeModeControl ||
          storedData.fontSize !== transformed.fontSize ||
          storedData.dashboardAppearance !== transformed.dashboardAppearance;

        if (differsFromServer) {
          const age = getPersonalizationStorageAge(tenantId);
          if (age !== null && age < 15 * 60 * 1000) {
            return storedData;
          }
        }
      }
      
      // Save server response to localStorage for future use
      if (transformed) {
        savePersonalizationToStorage(tenantId, transformed);
      }
      
      return transformed;
    },
    enabled: options?.enabled !== false && !!tenantId && !isRestoring,
    staleTime: options?.staleTime ?? 5 * 60 * 1000,
    refetchOnMount: options?.refetchOnMount ?? false,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    retry: 1,
    initialData: options?.initialData ?? undefined,
    // Use localStorage data as placeholder if available (shows immediately while fetching)
    // Skip when SSR already seeded initialData so we do not treat it as a pending fetch.
    placeholderData: options?.initialData
      ? undefined
      : (localStorageData ?? undefined),
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
  };
};

